import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";

import {
  CalculatorRequest,
  CalculatorData,
} from "../../services/app/CalculatorRequest";

import formatNumber from "../../services/FormatNumber";

import "../../styles/app/CalculatorSection.css";
import DemoForm from "./DemoForm";
import { ThemeContext } from "../../contexts/ThemeContext";

const CalculatorSection: React.FC = () => {
  const { t } = useTranslation();

  const themeContext = useContext(ThemeContext);

  if (!themeContext) {
    throw new Error("ThemeContext must be used within a ThemeProvider");
  }

  const { isLightMode } = themeContext;

  const [calculatorData, setCalculatorData] = useState<CalculatorData>({
    id: 0,
    minQueries: 0,
    maxQueries: 0,
    minTime: 0,
    maxTime: 0,
    minCost: 0,
    maxCost: 0,
    botSaving: 0,
  });

  const [consultas, setConsultas] = useState(calculatorData.minQueries);
  const [tiempo, setTiempo] = useState(calculatorData.minTime);
  const [costo, setCosto] = useState(calculatorData.minCost);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchCalculatorData = async () => {
      try {
        const response = await CalculatorRequest();
        setCalculatorData(response.data);
        setConsultas(response.data.minQueries);
        setTiempo(response.data.minTime);
        setCosto(response.data.minCost);
      } catch (error) {
        console.error("Error fetching calculator data:", error);
      }
    };

    fetchCalculatorData();
  }, []);

  const minutosTotales = consultas * tiempo;

  let horasParaCostoActual = minutosTotales / 60;
  if (horasParaCostoActual < 160) {
    horasParaCostoActual = 160;
  }

  const costoActual = horasParaCostoActual * costo;

  const planes = [
    { precio: 999, minutos: 1800, add: 0.25 },
    { precio: 1699, minutos: 5400, add: 0.2 },
    { precio: 2899, minutos: 14400, add: 0.15 },
    { precio: 4699, minutos: 43200, add: 0.0 },
  ];

  let planSeleccionado = planes[0];

  if (minutosTotales < 5400) {
    planSeleccionado = planes[0];
  }

  if (minutosTotales >= 5400 && minutosTotales < 14400) {
    planSeleccionado = planes[1];
  }

  if (minutosTotales >= 14400 && minutosTotales < 43200) {
    planSeleccionado = planes[2];
  }

  if (minutosTotales >= 43200) {
    planSeleccionado = planes[3];
  }

  let pagoAdicional = 0;

  if (minutosTotales > planSeleccionado.minutos) {
    let minutosAdicionales = minutosTotales - planSeleccionado.minutos;
    pagoAdicional = minutosAdicionales * planSeleccionado.add;
  }

  const costoConBot = planSeleccionado.precio + pagoAdicional;

  const ahorroMensual = costoActual - costoConBot;
  const ahorroAnual = ahorroMensual * 12;
  const roi = costoConBot / ahorroMensual;

  return (
    <>
      <DemoForm show={showModal} onClose={() => setShowModal(false)} />
      <section
        className="calculator-section"
        data-theme={isLightMode ? "light" : "dark"}
      >
        <div className="calculator-heading">
          <h2 className="calculator-title">{t("calculatorSection.title")}</h2>
          <p className="calculator-desc">
            {t("calculatorSection.description")}
          </p>
        </div>

        <div className="calculator-container">
          <div className="calculator-input">
            <h3 className="calculator-subtitle">
              {t("calculatorSection.businessInfo")}
            </h3>

            <div className="calculator-group">
              <label className="calculator-label">
                {t("calculatorSection.avgQueries")}
              </label>
              <input
                type="range"
                min={calculatorData.minQueries}
                max={calculatorData.maxQueries}
                value={consultas}
                onChange={(e) => setConsultas(parseInt(e.target.value))}
                className="calculator-slider"
              />
              <div className="calculator-value">
                {consultas.toLocaleString()}
              </div>
            </div>

            <div className="calculator-group">
              <label className="calculator-label">
                {t("calculatorSection.avgTime")}
              </label>
              <input
                type="range"
                min={calculatorData.minTime}
                max={calculatorData.maxTime}
                value={tiempo}
                onChange={(e) => setTiempo(parseInt(e.target.value))}
                className="calculator-slider"
              />
              <div className="calculator-value">{tiempo} min</div>
            </div>

            <div className="calculator-group">
              <label className="calculator-label">
                {t("calculatorSection.hourlyCost")}
              </label>
              <input
                type="range"
                min={calculatorData.minCost}
                max={calculatorData.maxCost}
                value={costo}
                onChange={(e) => setCosto(parseInt(e.target.value))}
                className="calculator-slider"
              />
              <div className="calculator-value">${costo}</div>
            </div>

            <div className="calculator-minutes">
              <div className="calculator-minutes-row">
                <span>{t("calculatorSection.estimatedMinutes")}</span>
                <span className="calculator-cost-green">
                  {Math.round(minutosTotales).toLocaleString()} min
                </span>
              </div>
            </div>
          </div>

          <div className="calculator-output">
            <h3 className="calculator-subtitle">
              {t("calculatorSection.potentialSaving")}
            </h3>

            <div className="calculator-result">
              <div className="calculator-row">
                <span>{t("calculatorSection.currentMonthlyCost")}</span>
                <span className="calculator-cost-red">
                  ${formatNumber(costoActual)}
                </span>
              </div>

              <div className="calculator-row">
                <span>{t("calculatorSection.costWithBot")}</span>
                <span className="calculator-cost-green">
                  ${formatNumber(costoConBot)}
                </span>
              </div>

              <hr className="calculator-divider" />

              <div className="calculator-row calculator-highlight">
                <span>{t("calculatorSection.monthlySaving")}</span>
                <span className="calculator-cost-green">
                  ${formatNumber(ahorroMensual)}
                </span>
              </div>

              <div className="calculator-row">
                <span>{t("calculatorSection.annualSaving")}</span>
                <span className="calculator-cost-green">
                  ${formatNumber(ahorroAnual)}
                </span>
              </div>

              <div className="calculator-row">
                <span>{t("calculatorSection.roiMonths")}</span>
                <span className="calculator-cost-green">
                  {formatNumber(roi)}
                </span>
              </div>
            </div>

            <button
              className="calculator-btn"
              onClick={() => setShowModal(true)}
            >
              <FontAwesomeIcon icon={faCalendar} />{" "}
              {t("calculatorSection.scheduleDemo")}
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default CalculatorSection;
